import { Button, Stack, Typography, useThemeProps } from '@mui/material';
import {
  FORM_CHIP_GROUP_THEME_NAME,
  type FormChipSelectedLook,
} from '@onevision-resources/design-theme';
import { Check, Pencil, Plus } from 'lucide-react';
import { ChoiceChip } from 'src/components/Forms/FormChipGroup/form-chip-group.elements';
import {
  type ModalHeaderAction,
  ModalStep,
  useModalSteps,
} from 'src/components/ModalContainer';
import { useSmartSend } from 'src/pages/Conversations/Chat/InputField/smart-send/smart-send-context';
import { SmartSendTimeStep } from 'src/pages/Conversations/Chat/InputField/smart-send/smart-send-time-step';

interface SmartSendConfirmStepProps {
  /** Opened straight onto an existing schedule, with no time step below it to go back to. */
  canChangeTime?: boolean;
}

/** Step 2: the chosen time and the rules that cancel the scheduled send. */
export const SmartSendConfirmStep = ({ canChangeTime = false }: SmartSendConfirmStepProps) => {
  const { pushStep } = useModalSteps();
  const { schedule, isProcessingOption, isOnline, save, close } = useSmartSend();
  const { options, toggleCondition, selectedDateLabel, isValid } = schedule;
  const hasSelectedRules = options.some((option) => option.selected);
  const { selectedChipLook = 'filled' } = useThemeProps<
    object,
    { selectedChipLook?: FormChipSelectedLook },
    typeof FORM_CHIP_GROUP_THEME_NAME
  >({ props: {}, name: FORM_CHIP_GROUP_THEME_NAME });

  const headerActions: ModalHeaderAction[] | undefined = canChangeTime
    ? [
        {
          label: 'Change time',
          icon: <Pencil />,
          onClick: () => void pushStep(<SmartSendTimeStep afterPick="back" />),
          testId: 'smart-send-modal-change-time',
        },
      ]
    : undefined;

  return (
    <ModalStep
      title="Schedule send"
      headerActions={headerActions}
      actions={[
        { label: 'Cancel', kind: 'secondary', onClick: close },
        {
          label: 'Schedule send',
          kind: 'primary',
          onClick: save,
          disabled: !isValid || isProcessingOption || !isOnline,
          testId: 'smart-send-modal-confirm',
        },
      ]}
    >
      <Stack gap={2} data-testid="smart-send-confirm">
        <Typography variant="h6">{selectedDateLabel}</Typography>
        <Stack direction="row" gap={1} alignItems="flex-start">
          <Typography
            variant="body2"
            color="text.secondary"
            flexShrink={0}
            lineHeight="1.5rem"
          >
            Cancel if:
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap" flex={1}>
            {options.map((option) => (
              <ChoiceChip
                key={option.type}
                clickable
                label={option.label}
                onClick={() => toggleCondition(option.type)}
                aria-label={`Toggle rule: ${option.label}`}
                aria-pressed={option.selected}
                data-testid={`smart-send-modal-rule-${option.type}`}
                icon={option.selected ? <Check /> : <Plus />}
                selected={option.selected}
                look={selectedChipLook}
              />
            ))}
          </Stack>
          <Button
            size="small"
            color="inherit"
            disabled={!hasSelectedRules}
            onClick={() =>
              options
                .filter((option) => option.selected)
                .forEach((option) => toggleCondition(option.type))
            }
          >
            Clear
          </Button>
        </Stack>
        {isValid ? null : (
          <Typography variant="body2" color="error">
            Selected time has already passed. Pick another time.
          </Typography>
        )}
        {isOnline ? null : (
          <Typography variant="body2" color="text.secondary">
            You appear to be offline. Scheduling is disabled until connectivity is restored.
          </Typography>
        )}
      </Stack>
    </ModalStep>
  );
};
